define('presentation/signup/common/signupNavigationManager',[
    'businessServices/router/router',
    'constants/signupNumberTypeSelectionConstants',
    'settings/navigationConfiguration',
    'constants/signupSessionFlowConstants',
    'businessServices/signup/signupSessionSingleton',
    'common/storage/workSessionIdStorage',
], function() {
    const _navigationConfiguration = require('settings/navigationConfiguration');
    const _router = require('businessServices/router/router');
    const _signupSessionFlowConstants = require('constants/signupSessionFlowConstants');
    const _signupNumberTypeSelectionConstants = require('constants/signupNumberTypeSelectionConstants');
    const _signupSessionState = require('businessServices/signup/signupSessionSingleton');
    const _workSessionIdStorage = require('common/storage/workSessionIdStorage');

    let _signupSessionStore = null;
    let _disposables = [];
    let _numberTypeSelection = ko.observable(null);
    let _isBypassNumberSelection = ko.observable(false);

    const ROUTE_ID__SIGNUP_EMAIL = _navigationConfiguration.routesById.signupEmail.routeId;
    const ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE = _navigationConfiguration.routesById.signupPhoneNumberType.routeId;
    const ROUTE_ID__SIGNUP_PHONE_NUMBER_LOCAL =  _navigationConfiguration.routesById.signupPhoneNumberLocal.routeId;
    const ROUTE_ID__SIGNUP_PHONE_NUMBER_TOLL_FREE =  _navigationConfiguration.routesById.signupPhoneNumberTollfree.routeId;
    const ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT =  _navigationConfiguration.routesById.signupPhoneNumberPort.routeId;
    const ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING =  _navigationConfiguration.routesById.signupPhoneNumberPortBilling.routeId;
    const ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION =  _navigationConfiguration.routesById.signupPhoneNumberPortAuthorization.routeId;
    const ROUTE_ID__SIGNUP_PLANS = _navigationConfiguration.routesById.signupPlans.routeId;
    const ROUTE_ID__SIGNUP_PROFILE =  _navigationConfiguration.routesById.signupProfile.routeId;
    const ROUTE_ID__SIGNUP_SECURITY =  _navigationConfiguration.routesById.signupSecurity.routeId;
    const ROUTE_ID__SIGNUP_PAYMENT_METHOD =  _navigationConfiguration.routesById.signupPaymentMethod.routeId;
    const ROUTE_ID__SIGNUP_SCHEDULING = _navigationConfiguration.routesById.signupScheduling.routeId;
    const ROUTE_ID__SIGNUP_SUCCESS = _navigationConfiguration.routesById.signupSuccess.routeId;
    const ROUTE_ID__SIGNUP_ERROR = _navigationConfiguration.routesById.signupError.routeId;

    const FLOW_A = _signupSessionFlowConstants.flowA;
    const FLOW_B = _signupSessionFlowConstants.flowB;
    const FLOW_C = _signupSessionFlowConstants.flowC;
    const FLOW_D = _signupSessionFlowConstants.flowD;
    const FLOW_AGENT = _signupSessionFlowConstants.flowAgent;

    const PREVIOUS_OPTIONAL_ROUTES = "previousOptionalRoutes";
    const NEXT_OPTIONAL_ROUTES = "nextOptionalRoutes";

    const _isStandardFlow = (flowId) => flowId === FLOW_A || flowId === FLOW_AGENT;

    const _isAgentSignup = () => {
        const workSessionId = _workSessionIdStorage.getWorkSessionId();
        return workSessionId !== null && workSessionId !== undefined;
    };

    // Mirrors _hasOnlyTelephonyPackage in signupPhoneNumberTypeViewModel.
    const _hasOnlyTelephonyPackage = () => {
        const signupDocument = _signupSessionState.signupDocument;
        return !!(signupDocument &&
            signupDocument.telephonyPackageId &&
            !signupDocument.liveReceptionistPackageId &&
            !signupDocument.webChatPackageId &&
            !signupDocument.outboundCallingPackageId);
    };

    // The number segment. Agents always choose their own number; self-service, only telephony-only
    // customers do - anyone who also bought Live Call Answering, Web Chat or Outbound Calling has
    // one assigned at payment, so the number screens are not part of their flow at all.
    const _isNumberSegmentIncluded = () => {
        if (_isAgentSignup()) {
            return true;
        }

        const isTelephonyOnlyOffering = _signupSessionState.isTelephonyOnlyOffering();

        return isTelephonyOnlyOffering === null ?
            _hasOnlyTelephonyPackage() :
            isTelephonyOnlyOffering;
    };

    const _buildStandardFlow = (flowId) => ({
        routes: [
            () => _constructRoute(ROUTE_ID__SIGNUP_PLANS, flowId),
            () => _constructRoute(ROUTE_ID__SIGNUP_PROFILE, flowId),
            () => _constructRoute(ROUTE_ID__SIGNUP_SECURITY, flowId),
            () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE, flowId),
            () => _constructRouteByNumberType(flowId),
            () => _constructRoute(ROUTE_ID__SIGNUP_PAYMENT_METHOD, flowId)
        ],
        routeIndexes: {
            [ROUTE_ID__SIGNUP_PLANS]: 0,
            [ROUTE_ID__SIGNUP_PROFILE]: 1,
            [ROUTE_ID__SIGNUP_SECURITY]: 2,
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE]: 3,
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_LOCAL]: 4,
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_TOLL_FREE]: 4,
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT]: 4,
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: 4,
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION]: 4,
            [ROUTE_ID__SIGNUP_PAYMENT_METHOD]: 5
        },
        [PREVIOUS_OPTIONAL_ROUTES]: {
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING, flowId),
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT, flowId)
        },
        [NEXT_OPTIONAL_ROUTES]: {
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING, flowId),
            [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION, flowId)
        }
    });

    const SIGNUP_FLOWS = {
        [FLOW_A]: _buildStandardFlow(FLOW_A),
        [FLOW_AGENT]: _buildStandardFlow(FLOW_AGENT),
        [FLOW_B]: {
            routes: [
                () => _constructRouteByNumberType(FLOW_B),
                () => _constructRoute(ROUTE_ID__SIGNUP_EMAIL, FLOW_B),
                () => _constructRoute(ROUTE_ID__SIGNUP_PROFILE, FLOW_B),
                () => _constructRoute(ROUTE_ID__SIGNUP_PLANS, FLOW_B),
                () => _constructRoute(ROUTE_ID__SIGNUP_SECURITY, FLOW_B),
                () => _constructRoute(ROUTE_ID__SIGNUP_PAYMENT_METHOD, FLOW_B)
            ],
            routeIndexes: {
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_LOCAL]: 0,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_TOLL_FREE]: 0,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT]: 0,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: 0,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION]: 0,
                [ROUTE_ID__SIGNUP_EMAIL]: 1,
                [ROUTE_ID__SIGNUP_PROFILE]: 2,
                [ROUTE_ID__SIGNUP_PLANS]: 3,
                [ROUTE_ID__SIGNUP_SECURITY]: 4,
                [ROUTE_ID__SIGNUP_PAYMENT_METHOD]: 5
            },
            [PREVIOUS_OPTIONAL_ROUTES]: {
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING, FLOW_B),
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT, FLOW_B)
            },
            [NEXT_OPTIONAL_ROUTES]: {
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING, FLOW_B),
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION, FLOW_B)
            }
        },
        [FLOW_C]: {
            routes: [
                () => _constructRoute(ROUTE_ID__SIGNUP_EMAIL, FLOW_C),
                () => _constructRoute(ROUTE_ID__SIGNUP_PROFILE, FLOW_C),
                () => _constructRoute(ROUTE_ID__SIGNUP_PLANS, FLOW_C),
                () => _constructRoute(ROUTE_ID__SIGNUP_SECURITY, FLOW_C),
                () => _constructRoute(ROUTE_ID__SIGNUP_PAYMENT_METHOD, FLOW_C)
            ],
            routeIndexes: {
                [ROUTE_ID__SIGNUP_EMAIL]: 0,
                [ROUTE_ID__SIGNUP_PROFILE]: 1,
                [ROUTE_ID__SIGNUP_PLANS]: 2,
                [ROUTE_ID__SIGNUP_SECURITY]: 3,
                [ROUTE_ID__SIGNUP_PAYMENT_METHOD]: 4
            },
            [PREVIOUS_OPTIONAL_ROUTES]: {},
            [NEXT_OPTIONAL_ROUTES]: {}
        },
        [FLOW_D]: {
            routes: [
                () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE, FLOW_D),
                () => _constructRouteByNumberType(FLOW_D),
                () => _constructRoute(ROUTE_ID__SIGNUP_EMAIL, FLOW_D),
                () => _constructRoute(ROUTE_ID__SIGNUP_PROFILE, FLOW_D),
                () => _constructRoute(ROUTE_ID__SIGNUP_PLANS, FLOW_D),
                () => _constructRoute(ROUTE_ID__SIGNUP_SECURITY, FLOW_D),
                () => _constructRoute(ROUTE_ID__SIGNUP_PAYMENT_METHOD, FLOW_D)
            ],
            routeIndexes: {
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE]: 0,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_LOCAL]: 1,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_TOLL_FREE]: 1,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT]: 1,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: 1,
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION]: 1,
                [ROUTE_ID__SIGNUP_EMAIL]: 2,
                [ROUTE_ID__SIGNUP_PROFILE]: 3,
                [ROUTE_ID__SIGNUP_PLANS]: 4,
                [ROUTE_ID__SIGNUP_SECURITY]: 5,
                [ROUTE_ID__SIGNUP_PAYMENT_METHOD]: 6
            },
            [PREVIOUS_OPTIONAL_ROUTES]: {
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING, FLOW_D),
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT, FLOW_D)
            },
            [NEXT_OPTIONAL_ROUTES]: {
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING, FLOW_D),
                [ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING]: () => _constructRoute(ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION, FLOW_D)
            }
        }
    };

    const _constructRoute = (routeId, flowId) => {
        let url = _navigationConfiguration.routesById[routeId].url;
        return url.replace(":flow", flowId);
    };

    const _constructRouteByNumberType = (flowId) => {
        let url = "";
        switch (_numberTypeSelection()) {
            case _signupNumberTypeSelectionConstants.local:
                url = _navigationConfiguration.routesById[ROUTE_ID__SIGNUP_PHONE_NUMBER_LOCAL].url;
                break;
            case _signupNumberTypeSelectionConstants.tollFree:
                url = _navigationConfiguration.routesById[ROUTE_ID__SIGNUP_PHONE_NUMBER_TOLL_FREE].url;
                break;
            case _signupNumberTypeSelectionConstants.port:
                url = _navigationConfiguration.routesById[ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT].url;
                break;
            default:
                url = _navigationConfiguration.routesById[ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE].url;
                break;
        }
        return url.replace(":flow", flowId);
    };

    const PORT_ROUTE_IDS = [
        ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT,
        ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_BILLING,
        ROUTE_ID__SIGNUP_PHONE_NUMBER_PORT_AUTHORIZATION
    ];

    const _pushNumberPickerRouteIds = (routeIds) => {
        if (_isNumberSegmentIncluded() === false) {
            return;
        }

        switch (_numberTypeSelection()) {
            case _signupNumberTypeSelectionConstants.port:
                PORT_ROUTE_IDS.forEach(portRouteId => routeIds.push(portRouteId));
                break;
            case _signupNumberTypeSelectionConstants.tollFree:
                routeIds.push(ROUTE_ID__SIGNUP_PHONE_NUMBER_TOLL_FREE);
                break;
            default:
                routeIds.push(ROUTE_ID__SIGNUP_PHONE_NUMBER_LOCAL);
                break;
        }
    };

    const _getVisibleRouteIds = () => {
        const routeIds = [
            ROUTE_ID__SIGNUP_PLANS,
            ROUTE_ID__SIGNUP_PROFILE,
            ROUTE_ID__SIGNUP_SECURITY
        ];

        if (_isNumberSegmentIncluded()) {
            routeIds.push(ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE);
            _pushNumberPickerRouteIds(routeIds);
        }

        routeIds.push(ROUTE_ID__SIGNUP_PAYMENT_METHOD);

        return routeIds;
    };

    const _getStepPosition = () => {
        const flowId = _getFlowId();
        const flow = SIGNUP_FLOWS[flowId];
        const routeId = _getRouteId();

        // Flows B, C and D keep the original fixed-length arithmetic.
        if (_isStandardFlow(flowId) === false) {
            return { step: flow.routeIndexes[routeId] + 1, totalSteps: flow.routes.length };
        }

        const visibleRouteIds = _getVisibleRouteIds();
        const position = visibleRouteIds.indexOf(routeId);

        return {
            step: position === -1 ? flow.routeIndexes[routeId] + 1 : position + 1,
            totalSteps: visibleRouteIds.length
        };
    };

    // Stored before navigating away so the next page's bar animates from where this one left it.
    // Must stay in step with signupPageIndicatorWidget's target, or a refresh starts from the
    // wrong place.
    const _getProgressPercentage = () => {
        const { step, totalSteps } = _getStepPosition();
        if (totalSteps <= 0) {
            return 0;
        }
        return step / totalSteps * 100;
    };

    const _getFlowStartUrl = (flowId, queryString = null) => {
        const flow = _getFlow(flowId);

        if (typeof flow === 'undefined') {
            return null;
        }

        const url = flow.routes[0]();

        return queryString ? `${url}?${queryString}` : url;
    };

    const _navigateToFlowStart = (flowId, queryString = null) => {
        const url = _getFlowStartUrl(flowId, queryString);

        if (url === null) {
            return;
        }

        _router.navigate(url);
    };

    const _navigateToDefaultSignupPage = () => _navigateToFlowStart(FLOW_A);

    const _previousPage = () =>  {
        const previousPageUrl = _getPreviousPageUrl();

        if (previousPageUrl !== null) {
            _signupSessionStore.setProgressPercentage(_getProgressPercentage())
                .done(() => {
                    _router.navigate(previousPageUrl);
                });
        }
    };

    const _nextPage = () => {
        const nextPageUrl = _getNextPageUrl();

        if (nextPageUrl !== null) {
            _signupSessionStore.setProgressPercentage(_getProgressPercentage())
                .done(() => {
                    _router.navigate(nextPageUrl);
                });
        }
    };

    const _getFlowId = () => {
        return _router.activeInstruction().params[0];
    };

    const _getRouteId = () => {
        return _router.activeInstruction().config.customSettings.routeId;
    };

    const _getFlow = (flowId) => {
        return SIGNUP_FLOWS[flowId];
    };

    const _isFlowStartPage = () => {
        const routeId = _getRouteId();
        const flowId = _getFlowId();
        const flow = _getFlow(flowId);

        return flow && flow.routeIndexes[routeId] === 0;
    };

    const _isPreviousPageValid = () => {
        return _getPreviousPageUrl() !== null;
    };

    const _isNextPageValid = () => {
        return _getNextPageUrl() !== null;
    };

    const _getPreviousPageUrl = () => {
        const routeId = _getRouteId();
        const flowId = _getFlowId();
        const optionalPageUrl = _getOptionalPageUrl(routeId, flowId, PREVIOUS_OPTIONAL_ROUTES);

        if (optionalPageUrl !== null) {
            return optionalPageUrl;
        }

        const flow = SIGNUP_FLOWS[flowId];
        let previousPageIndex = flow.routeIndexes[routeId] - 1;

        if (_isStandardFlow(flowId) && routeId === ROUTE_ID__SIGNUP_PAYMENT_METHOD) {
            if (_isNumberSegmentIncluded() === false) {
                previousPageIndex -= 2;
            } else if (_isBypassNumberSelection() === true) {
                previousPageIndex--;
            }
        }

        if (previousPageIndex >= 0) {
            return flow.routes[previousPageIndex]();
        }

        return null;
    };

    const _getNextPageUrl = () => {
        const routeId = _getRouteId();
        const flowId = _getFlowId();
        const optionalPageUrl = _getOptionalPageUrl(routeId, flowId, NEXT_OPTIONAL_ROUTES);

        if (optionalPageUrl !== null) {
            return optionalPageUrl;
        }

        const flow = SIGNUP_FLOWS[flowId];
        let nextPageIndex = flow.routeIndexes[routeId] + 1;

        if (_isStandardFlow(flowId) && routeId === ROUTE_ID__SIGNUP_SECURITY && _isNumberSegmentIncluded() === false) {
            nextPageIndex += 2;
        } else if (routeId === ROUTE_ID__SIGNUP_PHONE_NUMBER_TYPE && _isBypassNumberSelection() === true) {
            nextPageIndex ++;
        }

        if (nextPageIndex < flow.routes.length) {
            return flow.routes[nextPageIndex]();
        }

        return null;
    };

    const _getOptionalPageUrl = (routeId, flowId, optionalRoutes) => {
        const optionalUrlFunction = SIGNUP_FLOWS[flowId][optionalRoutes][routeId];
        if (typeof optionalUrlFunction !== 'undefined') {
            return optionalUrlFunction();
        }
        return null;
    };

    const _navigateToRoute = (routeId, flowId) => {
        const flow = SIGNUP_FLOWS[flowId];
        if (typeof flow === 'undefined') {
            return;
        }

        const routeIndex = flow.routeIndexes[routeId];
        if (typeof routeIndex === 'undefined') {
            return;
        }

        const url = _constructRoute(routeId, flowId);
        _router.navigate(url);
    };

    const _navigateToSuccessPage = () => {
        const flowId = _getFlowId();
        _router.navigate(_constructRoute(ROUTE_ID__SIGNUP_SUCCESS, flowId));
    };

    const _navigateToSchedulingPage = () => {
        const flowId = _getFlowId();
        _router.navigate(_constructRoute(ROUTE_ID__SIGNUP_SCHEDULING, flowId));
    };

    const _navigateToErrorPage = () => {
        _router.navigate(_constructRoute(ROUTE_ID__SIGNUP_ERROR));
    };

    const _detached = () => {
        _disposables.forEach(subscription => subscription.dispose());
        _disposables = [];
    };

    const _initialize = () => {
        const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');
        _signupSessionStore = new SignupSessionStoreConstructor();
        _signupSessionStore.init();

        _signupSessionStore.get(false)
            .done(() => {
                _numberTypeSelection(_signupSessionState.numberTypeSelection());
                _isBypassNumberSelection(_signupSessionState.isBypassNumberSelection());
                _disposables.push(_signupSessionState.numberTypeSelection.subscribe(_numberTypeSelection));
                _disposables.push(_signupSessionState.isBypassNumberSelection.subscribe(_isBypassNumberSelection));
            });
    };

    return function() {
        let self = this;

        self.init = _initialize;
        self.detached = _detached;

        self.getFlowId = _getFlowId;
        self.getRouteId = _getRouteId;
        self.getFlow = _getFlow;
        self.getFlowStartUrl = _getFlowStartUrl;
        self.getStepPosition = _getStepPosition;
        self.isFlowStartPage = _isFlowStartPage;
        self.isPreviousPageValid = _isPreviousPageValid;
        self.isNextPageValid = _isNextPageValid;

        self.navigateToDefaultSignupPage  = _navigateToDefaultSignupPage;
        self.navigateToFlowStart = _navigateToFlowStart;
        self.navigateToRoute = _navigateToRoute;
        self.navigateToSchedulingPage = _navigateToSchedulingPage;
        self.navigateToSuccessPage = _navigateToSuccessPage;
        self.navigateToErrorPage = _navigateToErrorPage;
        self.previousPage = _previousPage;
        self.nextPage = _nextPage;
    };
});

